// 统一的请求发送
import axios from 'axios'
import qs from 'qs'
import { message, Modal, notification } from 'ant-design-vue'
import { APP_CONFIG } from '@/config/index'
import tool from '@/utils/tool'
import { useGlobalStore } from '@/store/global'

// 以下这些code需要重新登录
const reloadCodes = [401, 1011007, 1011008]
const errorCodeMap = {
	400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
	401: '用户没有权限（令牌、用户名、密码错误）。',
	403: '用户得到授权，但是访问是被禁止的。',
	404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
	406: '请求的格式不可得。',
	410: '请求的资源被永久删除，且不会再得到的。',
	422: '当创建一个对象时，发生一个验证错误。',
	500: '服务请求发生错误，请联系管理员。',
	502: '网关错误。',
	503: (() => {
		const now = new Date()
		const day = now.getDay()
		const hour = now.getHours()
		const isWorkday = day >= 1 && day <= 5
		const isWorkHour = (hour >= 9 && hour < 12) || (hour >= 14 && hour < 17)
		return isWorkday && isWorkHour ? '服务不可用，请联系管理员。' : '系统升级维护中，如有疑问请联系管理员。'
	})(),
	504: '网关超时。'
}
// 定义一个重新登录弹出窗的变量
const loginBack = ref(false)
// 创建 axios 实例
const service = axios.create({
	baseURL: APP_CONFIG.API_URL, // api base_url
	timeout: APP_CONFIG.TIMEOUT // 请求超时时间
})

// HTTP request 拦截器
service.interceptors.request.use(
	(config) => {
		const token = tool.token.get()
		if (token) {
			config.headers[APP_CONFIG.TOKEN_NAME] = APP_CONFIG.TOKEN_PREFIX + token

			// 移动端不加载 pageoffice.js（index.html 按 UA 注入），必须从 window 上取，
			// 否则裸标识符 POBrowser 未声明会抛 ReferenceError，拖垮所有请求
			window.POBrowser?.setHeader?.(APP_CONFIG.TOKEN_NAME, APP_CONFIG.TOKEN_PREFIX + token)
		}
		if (!APP_CONFIG.REQUEST_CACHE && config.method === 'get') {
			config.params = config.params || {}
			config.params._ = new Date().getTime()
		}
		Object.assign(config.headers, APP_CONFIG.HEADERS)
		return config
	},
	(error) => {
		console.log(1)
		return Promise.reject(error)
	}
)

// 保持重新登录Modal的唯一性
const error = () => {
	loginBack.value = true
	Modal.error({
		title: '提示：',
		okText: '重新登录',
		content: '登录已失效， 请重新登录',
		onOk: () => {
			loginBack.value = false
			useGlobalStore().clear()
			window.location.reload()
		}
	})
}

// HTTP response 拦截器
service.interceptors.response.use(
	(response) => {
		// 配置了blob，不处理直接返回文件流
		if (response.config.responseType === 'blob') {
			if (response.status === 200) {
				return response
			} else {
				message.warning('文件下载失败或此文件不存在')
				return
			}
		}
		const data = response.data
		const code = data.code
		if (reloadCodes.includes(code)) {
			if (!loginBack.value) {
				error()
			}
			return
		}
		if (data.repCode) {
			return Promise.resolve(data)
		} else if (code !== 200) {
			const customErrorMessage = response.config.customErrorMessage
			// silentError: 调用方自行 catch 处理（如移动端首页待办卡对无权限接口容错），不弹全局错误提示
			if (!response.config.silentError) {
				message.error(customErrorMessage || data.msg)
			}
			return Promise.reject(data)
			// 自定义错误提示，覆盖后端返回的message
			// 使用示例：
			// export function customerList (data) {
			//   return request('list', data, 'get', {
			//     customErrorMessage: '自定义错误消息提示'
			//   });
			// }
		} else {
			// 统一成功提示
			const responseUrl = response.config.url
			const apiNameArray = [
				'add',
				'edit',
				'delete',
				'update',
				'grant',
				'reset',
				'stop',
				'pass',
				'disable',
				'enable',
				'revoke',
				'suspend',
				'active',
				// 'turn',
				'adjust',
				'reject',
				'saveDraft',
				'start'
			]
			apiNameArray.forEach((apiName) => {
				if (responseUrl.includes(apiName)) {
					message.success(data.msg)
				}
			})
		}

		return Promise.resolve(data.data)
	},
	(error) => {
		if (error) {
			const status = 503
			const description = errorCodeMap[status]
			notification.error({
				message: '请求错误',
				description
			})
			return Promise.reject(status)
		}
	}
)

// 判断是否为纯普通对象（Plain Old JavaScript Object）
// 仅对纯对象做 trim，其他所有内置/第三方类实例（FormData、Date、File、Blob、Map、Set、ArrayBuffer 等）一律原样返回
const isPlainObject = (obj) => {
	if (typeof obj !== 'object' || obj === null) return false
	const tag = Object.prototype.toString.call(obj)
	return tag === '[object Object]'
}

const trimParams = (params) => {
	// 基础类型 & 函数 & null 直接返回
	if (typeof params !== 'object' || params === null) return params
	// 数组：递归处理每个元素
	if (Array.isArray(params)) return params.map(trimParams)
	// 非纯对象（FormData / Date / File / Blob / Map / Set / RegExp / ArrayBuffer / 第三方库实例等）原样透传
	if (!isPlainObject(params)) return params
	const result = {}
	for (const key in params) {
		if (Object.prototype.hasOwnProperty.call(params, key)) {
			const val = params[key]
			result[key] = typeof val === 'string' ? val.trim() : trimParams(val)
		}
	}
	return result
}

// 适配器, 用于适配不同的请求方式
export const baseRequest = (url, value = {}, method = 'post', options = {}) => {
	const trimmedValue = trimParams(value)
	if (method === 'post') {
		return service.post(url, trimmedValue, options)
	} else if (method === 'get') {
		return service.get(url, { params: trimmedValue, ...options })
	} else if (method === 'formdata') {
		return service.post(url, qs.stringify(trimmedValue), {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			...options
		})
	} else {
		return service({
			method: method,
			url: url,
			data: trimmedValue,
			...options
		})
	}
}

// 模块内的请求, 会自动加上模块的前缀
export const moduleRequest =
	(moduleUrl) =>
	(url, ...arg) => {
		return baseRequest(moduleUrl + url, ...arg)
	}

export default service
